'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';

import { adminNavSections } from './adminNavConfig.js';
import { resolveHeaderMessage } from './resolveHeaderMessage.js';

const AdminNavMenu = ({ messages = {} }) => {
  const pathname = usePathname() || '';
  const isOnAdminRoute = pathname.startsWith('/admin');
  const [openSectionKey, setOpenSectionKey] = useState(null);
  const [hoverSectionKey, setHoverSectionKey] = useState(null);
  const hoverClearTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (hoverClearTimeoutRef.current) {
        clearTimeout(hoverClearTimeoutRef.current);
      }
    };
  }, []);

  const activeSectionKey = useMemo(() => {
    if (!isOnAdminRoute) {
      return null;
    }

    let bestMatch = { sectionKey: null, matchLength: -1 };

    for (const section of adminNavSections) {
      for (const item of section.items) {
        if (!item?.to) {
          continue;
        }

        const isMatch =
          pathname === item.to || pathname.startsWith(`${item.to}/`);

        if (!isMatch) {
          continue;
        }

        if (item.to.length > bestMatch.matchLength) {
          bestMatch = { sectionKey: section.key, matchLength: item.to.length };
        }
      }
    }

    return bestMatch.sectionKey;
  }, [isOnAdminRoute, pathname]);

  useEffect(() => {
    if (!isOnAdminRoute) {
      setOpenSectionKey(null);
      setHoverSectionKey(null);
      return;
    }

    setOpenSectionKey(activeSectionKey);
    setHoverSectionKey(activeSectionKey);
  }, [activeSectionKey, isOnAdminRoute, pathname]);

  const interactionSectionKey = openSectionKey ?? hoverSectionKey;
  const resolvedActiveSectionKey = interactionSectionKey ?? activeSectionKey;

  return (
    <>
      {adminNavSections.map((section) => {
        const sectionIsActive = section.key === resolvedActiveSectionKey;
        const sectionIsOpen = openSectionKey === section.key;

        return (
          <DropdownButton
            key={section.key}
            drop="start"
            autoClose="outside"
            variant="transparent"
            className={`admin-submenu${sectionIsActive ? ' is-active' : ''}`}
            show={sectionIsOpen}
            onToggle={(nextShow) => {
              if (nextShow) {
                setOpenSectionKey(section.key);
                setHoverSectionKey(section.key);
                return;
              }

              setOpenSectionKey((current) =>
                current === section.key ? null : current,
              );
            }}
            onSelect={() => {
              setOpenSectionKey(null);
            }}
            onMouseEnter={() => {
              if (hoverClearTimeoutRef.current) {
                clearTimeout(hoverClearTimeoutRef.current);
              }
              setHoverSectionKey(section.key);
            }}
            onMouseLeave={() => {
              if (openSectionKey) {
                return;
              }

              if (hoverClearTimeoutRef.current) {
                clearTimeout(hoverClearTimeoutRef.current);
              }

              hoverClearTimeoutRef.current = setTimeout(() => {
                setHoverSectionKey((current) =>
                  current === section.key ? null : current,
                );
              }, 120);
            }}
            title={
              <>
                <span aria-hidden="true" className="admin-submenu__chevron">
                  ‹
                </span>
                <span aria-hidden="true" className="position-relative">
                  {section.icon}
                </span>
                <span className="dropdown-label flex-grow-1">
                  {resolveHeaderMessage(
                    messages,
                    section.titleKey,
                    section.key,
                  )}
                </span>
              </>
            }
          >
            {section.items.map((item) => (
              <Dropdown.Item
                key={item.to}
                as={Link}
                href={item.to}
                className="d-flex align-items-center gap-2"
              >
                <span aria-hidden="true">{item.icon}</span>
                <span className="dropdown-label flex-grow-1">
                  {resolveHeaderMessage(messages, item.labelKey, item.labelKey)}
                </span>
              </Dropdown.Item>
            ))}
          </DropdownButton>
        );
      })}
    </>
  );
};

export default AdminNavMenu;
